<template>
  <v-card flat>
    <v-card-text>
      <!-- Большая кнопка -->
      <v-row class="mb-4">
        <v-col cols="12" class="text-center d-flex justify-center">
          <v-btn
            x-large
            color="primary"
            :loading="isChecking"
            :disabled="isChecking"
            @click="handleStartCheck"
            class="px-12 mr-2 w-100"
          >
            <v-icon left>mdi-shield-search</v-icon>
            {{ $t('parser_api.start_check') }}
          </v-btn>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" color="grey">mdi-information-outline</v-icon>
            </template>
            <p v-html="$t('parser_api.pricing_description', { price: main_company.plan.check_client_reliability_cost, currency: currency })"></p>
          </v-tooltip>

          <!-- Индикатор подключения WebSocket -->
          <div
            v-if="isChecking"
            :class="'pulse mt-3 mx-2 ' + (connected ? 'pulse_success' : 'pulse_error')"
            v-tooltip="{
              content: connected ? 'Подключено' : 'Отключено',
              placement: 'bottom-center',
              classes: ['info'],
              targetClasses: ['it-has-a-tooltip'],
              delay: { show: 500, hide: 500 }
            }"
          ></div>
        </v-col>
      </v-row>

      <!-- Real-time прогресс -->
      <v-row v-if="progress > 0" class="mb-4">
        <v-col cols="12">
          <v-progress-linear
            :value="progress"
            height="25"
            color="primary"
            striped
          >
            <template v-slot:default>
              <strong>{{ progress.toFixed(1) }}%</strong>
            </template>
          </v-progress-linear>
          
          <p class="text-center mt-2">
            {{ completedChecks }} / {{ totalChecks }} {{ $t('parser_api.completed_checks') }}
          </p>
          
          <!-- Текущая проверка (скрываем после завершения всех проверок) -->
          <v-alert
            v-if="currentCheck && progress < 100"
            :type="currentCheck.check_status === 'completed' ? 'success' : 'info'"
            dense
            class="mt-2"
          >
            {{ getCheckName(currentCheck.current_check) }}: {{ getCheckStatus(currentCheck.check_status) }}
          </v-alert>
        </v-col>
      </v-row>

      <!-- ML-индекс и отчеты (после завершения или если есть предыдущая проверка) -->
      <div v-if="loadedCheck">
        <v-row class="mb-4">
          <v-col cols="12" class="text-center">
            <v-chip
              :color="mlIndexColor"
              large
              label
              class="mr-2"
            >
              <v-icon left>mdi-chart-arc</v-icon>
              {{ $t('parser_api.ml_index') }}: {{ mlIndexLabel }}
            </v-chip>
            
            <v-tooltip bottom class="ml-2">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" color="grey">mdi-information-outline</v-icon>
              </template>
              <p v-html="$t('parser_api.ml_index_description')"></p>
            </v-tooltip>
            <div class="mt-2 text-caption grey--text">
              {{ $t('parser_api.last_checked') }}: {{ formatDate(loadedCheck.updated_at) }}
              <span v-if="loadedCheck.user">
                | {{ $t('parser_api.checked_by') }}: {{ loadedCheck.user.name }}
              </span>
            </div>
          </v-col>
        </v-row>

        <!-- Отчеты -->
        <v-expansion-panels v-model="activePanels" multiple>
          <!-- Экспресс-отчет -->
          <v-expansion-panel>
            <v-expansion-panel-header>
              <span class="text-h6">{{ $t('parser_api.express_report') }}</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <ClientCheckExpressReport :clientCheck="loadedCheck" @update-inn="updateClientInn" />
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- Полный отчет -->
          <v-expansion-panel>
            <v-expansion-panel-header>
              <span class="text-h6">{{ $t('parser_api.full_report') }}</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <ClientCheckFullReport :clientCheck="loadedCheck" @update-inn="updateClientInn" />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </v-card-text>
    
    <!-- Диалог подтверждения обновления ИНН -->
    <v-dialog v-model="innUpdateDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="warning">mdi-alert</v-icon>
          {{ $t('parser_api.confirm_inn_update_title') }}
        </v-card-title>
        
        <v-card-text>
          <v-alert type="info" text dense class="mb-3">
            {{ $t('parser_api.confirm_update_inn', { inn: newInnValue }) }}
          </v-alert>
          
          <div class="text-body-1">
            <div class="mb-2">
              <strong>{{ $t('parser_api.current_inn') }}:</strong> 
              {{ client.inn || $t('parser_api.not_filled') }}
            </div>
            <div>
              <strong>{{ $t('parser_api.new_inn') }}:</strong> 
              <span class="primary--text font-weight-bold ml-2">{{ newInnValue }}</span>
            </div>
          </div>
        </v-card-text>
        
        <v-divider></v-divider>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeInnDialog"
            :disabled="updatingInn"
          >
            {{ $t('buttons.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            @click="confirmUpdateInn"
            :loading="updatingInn"
            :disabled="updatingInn"
          >
            <v-icon left>mdi-check</v-icon>
            {{ $t('buttons.update') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import moment from 'moment'
import ClientCheckExpressReport from './ClientCheckExpressReport.vue'
import ClientCheckFullReport from './ClientCheckFullReport.vue'
import CableSubscriber from '@/mixins/CableSubscriber'

// #305: ml_index снимается в null на КАЖДОМ повторном запуске проверки
// (api ClientCheck#reset_checks_for_new_run, коммит 9af893f7). Отдельная функция,
// а не computed поверх computed: юниты зовут computed с синтетическим this,
// который цепочку не разрешает.
function readMlIndex(check) {
  const index = check ? check.ml_index : null
  return typeof index === 'number' && Number.isFinite(index) ? index : null
}

export default {
  name: 'ClientCheckTab',
  mixins: [CableSubscriber],
  components: {
    ClientCheckExpressReport,
    ClientCheckFullReport
  },
  props: {
    client: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loadedCheck: null,
      isChecking: false,
      connected: false,
      progress: 0,
      completedChecks: 0,
      totalChecks: 3,
      currentCheck: null,
      activePanels: [], // Открытые панели (0 - экспресс-отчет, 1 - полный отчет)
      
      // Диалог обновления ИНН
      innUpdateDialog: false,
      newInnValue: '',
      updatingInn: false,
      mlIndexDescriptionDialog: false
    }
  },
  computed: {
    // Без числа вердикта нет: ни красить в error, ни звать toFixed нельзя.
    mlIndexLabel() {
      const index = readMlIndex(this.loadedCheck)
      return index === null ? '—' : index.toFixed(1)
    },
    mlIndexColor() {
      const index = readMlIndex(this.loadedCheck)
      if (index === null) return 'grey'
      if (index >= 90) return 'success'
      if (index >= 75) return 'warning'
      return 'error'
    },
    main_company() {
      return this.$store.getters.getMainCompany
    },
    currency() {
      return this.$store.getters.getMainCompany.currency
    }
  },
  mounted() {
    // Загружаем существующую проверку (если есть)
    this.loadClientCheck()
    
    // Подписываемся на канал для получения обновлений (в т.ч. для rerun)
    this.subscribeToCheckUpdates()
  },
  beforeDestroy() {
    // Отписываемся при уничтожении компонента
    this.unsubscribeFromCheck()
  },
  methods: {
    async loadClientCheck() {
      try {
        const response = await this.axios.get(
          `/api/v1/parser_api/client_checks/${this.client.id}`,
          {
            headers: { Authorization: this.$store.getters.getAuthToken }
          }
        )
        this.loadedCheck = response.data.client_check
      } catch (error) {
        console.error('Error loading client check:', error)
      }
    },
    
    async handleStartCheck() {
      // Проверяем баланс перед запуском
      const mainCompany = this.$store.getters.getMainCompany
      if (!mainCompany || mainCompany.balance <= 0) {
        this.$notify(
          this.$t('parser_api.insufficient_balance_title'),
          this.$t('parser_api.insufficient_balance_message'),
          'warning',
          5000
        )
        return
      }
      
      this.isChecking = true
      this.progress = 0
      this.completedChecks = 0
      this.currentCheck = null
      this.activePanels = [] // Закрываем все панели при запуске проверки
      
      try {
        // Запускаем проверку на бэкенде
        const response = await this.axios.post(
          `/api/v1/parser_api/client_checks/${this.client.id}/start`,
          {},
          {
            headers: { Authorization: this.$store.getters.getAuthToken }
          }
        )
        
        const { client_id, success } = response.data
        
        if (success && client_id) {
          // Подписываемся на WebSocket канал для получения прогресса (held ref, same-id no-op)
          this.subscribeToClientCheckChannel(client_id)

          console.log('✅ Subscribed to check for client:', client_id)
        } else {
          throw new Error('Failed to start check')
        }
      } catch (error) {
        console.error('Error starting check:', error)
        this.isChecking = false
        
        // Если есть конкретная ошибка от бэкенда, показываем её
        let errorMessage = this.$t('parser_api.check_failed')
        if (error.response && error.response?.data && error.response?.data.error) {
          errorMessage = error.response?.data.error
        }
        
        this.$notify(
          this.$t('errors.error'),
          errorMessage,
          'error',
          5000
        )
      }
    },
    
    // Native ClientCheckChannel handlers (moved 1:1 from the old declarative channel block).
    onClientCheckConnected() {
      console.log('Connected to ClientCheckChannel')
      this.connected = true
    },
    onClientCheckDisconnected() {
      console.log('Disconnected from ClientCheckChannel')
      this.connected = false
    },
    onClientCheckReceived(data) {
      console.log('ClientCheck progress received:', data)

      // Обновляем прогресс
      this.progress = data.progress || 0
      this.completedChecks = data.completed_checks || 0
      this.totalChecks = data.total_checks || 3

      // Текущая проверка
      if (data.current_check) {
        this.currentCheck = data

        // ВАЖНО: Обновляем loadedCheck в реальном времени для отдельных проверок
        if (this.loadedCheck && data.check_status) {
          const checkType = data.current_check
          const statusField = `${checkType}_status`
          const dataField = `${checkType}_data`
          const scoreField = `${checkType}_score`

          // Обновляем статус проверки
          this.$set(this.loadedCheck, statusField, data.check_status)

          // Обновляем данные проверки если есть
          if (data.check_data) {
            this.$set(this.loadedCheck, dataField, data.check_data)
          }

          // Обновляем score если есть
          if (data.check_data && data.check_data.score !== undefined) {
            this.$set(this.loadedCheck, scoreField, data.check_data.score)
          }

          // Обновляем общие счетчики
          this.$set(this.loadedCheck, 'completed_checks', data.completed_checks)
          this.$set(this.loadedCheck, 'total_checks', data.total_checks)
        }
      }

      // Проверка завершена
      if (data.status === 'completed') {
        console.log('✅ Check completed!', data)
        this.isChecking = false
        this.loadClientCheck()

        // Открываем панель экспресс-отчета
        this.$nextTick(() => {
          this.activePanels = [0]
        })

        this.$notify(
          this.$t('parser_api.check_completed'),
          `${this.$t('parser_api.ml_index')}: ${data.ml_index?.toFixed(1)}`,
          'success',
          5000
        )

        // НЕ отписываемся от канала, чтобы можно было делать rerun
        // Отписка произойдет в beforeDestroy
      }

      // Проверка провалилась
      if (data.status === 'failed') {
        console.log('❌ Check failed', data)
        console.log('❌ Error message:', data.error)
        this.isChecking = false

        // Если есть конкретная ошибка от бэкенда, показываем её
        const errorMessage = data.error || this.$t('parser_api.check_failed')
        console.log('❌ Showing error:', errorMessage)
        this.$notify(this.$t('errors.error'), errorMessage, 'error', 5000)

        // НЕ отписываемся от канала, чтобы можно было делать rerun
      }
    },

    // Switch per client (held ref + same-id no-op). Both the mounted updates/rerun subscribe and
    // the post-start subscribe target the same client_id → a single live ClientCheckChannel sub.
    subscribeToClientCheckChannel(clientId) {
      if (!clientId) return
      if (this.clientCheckSub && this.subscribedClientId === clientId) return
      if (this.clientCheckSub) {
        this.$cableUnsubscribe(this.clientCheckSub)
        this.clientCheckSub = null
      }
      this.subscribedClientId = clientId
      this.clientCheckSub = this.$cableSubscribe(
        { channel: 'ClientCheckChannel', client_id: clientId },
        {
          connected: () => this.onClientCheckConnected(),
          disconnected: () => this.onClientCheckDisconnected(),
          received: (data) => this.onClientCheckReceived(data),
        },
      )
    },

    subscribeToCheckUpdates() {
      if (this.client && this.client.id) {
        this.subscribeToClientCheckChannel(this.client.id)
        console.log('✅ Subscribed to ClientCheckChannel for updates/rerun')
      }
    },

    unsubscribeFromCheck() {
      if (this.clientCheckSub) {
        this.$cableUnsubscribe(this.clientCheckSub)
        this.clientCheckSub = null
        this.subscribedClientId = null
        console.log('Unsubscribed from ClientCheckChannel')
      }
    },
    
    getCheckName(type) {
      const names = {
        pledge_registry: this.$t('parser_api.pledge_registry'),
        arbitr_cases: this.$t('parser_api.arbitr_cases'),
        bankruptcy: this.$t('parser_api.bankruptcy'),
        passport: this.$t('parser_api.passport_check'),
        inn: this.$t('parser_api.inn_check'),
        alimony: this.$t('parser_api.alimony_check'),
        driver: this.$t('parser_api.driver_check'),
        kbm: this.$t('parser_api.kbm_check'),
        fssp: this.$t('parser_api.fssp_check'),
        fssp_search: this.$t('parser_api.fssp_search_check'),
        blacklist: this.$t('parser_api.blacklist_check'),
        driver_fines: this.$t('parser_api.driver_fines_check')
      }
      return names[type] || type
    },
    
    getCheckStatus(status) {
      const statuses = {
        processing: this.$t('parser_api.status_processing'),
        completed: this.$t('parser_api.status_completed'),
        failed: this.$t('parser_api.status_failed'),
        skipped: this.$t('parser_api.status_skipped')
      }
      return statuses[status] || status
    },
    
    formatDate(date) {
      return moment(date).format('DD.MM.YYYY HH:mm')
    },
    
    updateClientInn(inn) {
      // Открываем диалог для подтверждения
      this.newInnValue = inn
      this.innUpdateDialog = true
    },
    
    closeInnDialog() {
      this.innUpdateDialog = false
      this.newInnValue = ''
      this.updatingInn = false
    },
    
    async confirmUpdateInn() {
      if (!this.newInnValue) return
      
      this.updatingInn = true
      
      try {
        // Обновляем ИНН клиента
        await this.axios.patch(
          `/api/v1/clients/${this.client.id}`,
          {
            clients: {
              inn: this.newInnValue
            }
          },
          {
            headers: { Authorization: this.$store.getters.getAuthToken }
          }
        )
        
        // Обновляем ИНН в РОДИТЕЛЕ — prop `client` не мутируем напрямую
        // (vue/no-mutating-props: prop one-way, мутация перетрётся на ре-рендере родителя).
        this.$emit('update-client-inn', this.newInnValue)

        // Обновляем данные клиента в родительском компоненте
        this.$emit('client-updated')
        
        // Уведомляем пользователя
        this.$notify(
          'Успешно',
          this.$t('parser_api.inn_updated_success', { inn: this.newInnValue }),
          'success',
          3000
        )
        
        // Закрываем диалог
        this.closeInnDialog()
        
        // Перезагружаем проверку для обновления данных
        await this.loadClientCheck()
      } catch (error) {
        console.error('Error updating client INN:', error)
        this.$notify(
          'Ошибка',
          this.$t('parser_api.inn_update_error'),
          'error',
          5000
        )
      } finally {
        this.updatingInn = false
      }
    }
  }
}
</script>

<style scoped>
/* Стили для pulse индикатора */
.pulse {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  animation: pulse-animation 2s infinite;
}

.pulse_success {
  background-color: #4caf50;
  box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
}

.pulse_error {
  background-color: #f44336;
  box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7);
}

@keyframes pulse-animation {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}
</style>

